import {
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi
} from '@angular/common/http';
import { NgModule, inject, provideAppInitializer } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import {
  languageAcceptHeaderInterceptor,
  provideI18n,
  provideI18nLocaleId
} from '@gruposaltaedu/liv-i18n/core';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgxsStoragePluginModule, StorageOption } from '@ngxs/storage-plugin';
import { NgxsModule } from '@ngxs/store';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { environment } from 'src/environments/environment';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import {
  APP_I18N_COUNTRY_TO_LANGUAGE_MAP,
  APP_I18N_DEFAULT_LANGUAGE,
  APP_I18N_SUPPORTED_LANGUAGES,
  registerAppI18nLocaleData
} from './core/app-locale';
import { CommonsModule } from './core/commons/commons.module';
import { provideGlobalErrorHandler } from './core/errors/handlers/global-error-handler.service';
import { httpInterceptorProviders } from './core/interceptors';
import { portfolioLocaleQueryInterceptor } from './core/interceptors/portfolio-locale-query.interceptor';
import { AppLoadingShellComponent } from './core/layout/app-loading-shell/app-loading-shell.component';
import { decrypt, encrypt } from './core/security/state-cypher';
import { ApiGatewayService } from './core/services/api/api-gateway.service';
import { ApiLivGatewayService } from './core/services/api/api-liv-gateway.service';
import { ConfigService } from './core/services/config.service';
import { GeneralService } from './core/services/general.service';
import { BookState } from './core/store/book/book.state';
import { BookPagesState } from './core/store/book-pages/book-pages.state';
import { KibanaState } from './core/store/kibana/kibana.state';
import { SessionState } from './core/store/session/session.state';
import { TeacherState } from './core/store/teacher/teacher.state';
import { UserState } from './core/store/user/user.state';
import { SharedModule } from './shared/shared.module';
import { provideOverlayStacking } from './ui/components/modal/overlay-stacking.provider';
import { TOAST_CONFIG, ToastConfig } from './ui/components/toast/toast-config';

registerAppI18nLocaleData();

export const configFactory = (configService: ConfigService) => {
  return (): Promise<void> => configService.loadConfig();
};

const PERSISTED_STATES = [SessionState, BookState, BookPagesState];
const I18N_STORAGE_KEY = '@liv-prof-pfo:language';
const STATES = [...PERSISTED_STATES, KibanaState, TeacherState, UserState];

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    SharedModule,
    AppLoadingShellComponent,
    FormsModule,
    ReactiveFormsModule,
    CommonsModule.forRoot(),
    AngularSvgIconModule.forRoot(),
    NgxsModule.forRoot(STATES, {
      developmentMode: !environment.production,
      selectorOptions: {
        injectContainerState: false //https://www.ngxs.io/advanced/options (injectContainerState false é recomendado p/ melhor performance)
      }
    }),
    NgxsStoragePluginModule.forRoot({
      storage: StorageOption.SessionStorage,
      namespace: '@liv-prof-pfo',
      keys: [...PERSISTED_STATES],
      serialize: encrypt,
      deserialize: decrypt
    }),
    NgbModule
  ],
  providers: [
    httpInterceptorProviders,
    ApiGatewayService,
    ApiLivGatewayService,
    GeneralService,
    provideGlobalErrorHandler(),
    provideOverlayStacking(),
    provideAppInitializer(() => {
      const initializerFn = configFactory(inject(ConfigService));
      return initializerFn();
    }),
    provideHttpClient(
      withInterceptors([
        languageAcceptHeaderInterceptor,
        portfolioLocaleQueryInterceptor
      ]),
      withInterceptorsFromDi()
    ),
    provideI18n({
      supportedLanguages: [...APP_I18N_SUPPORTED_LANGUAGES],
      defaultLanguage: APP_I18N_DEFAULT_LANGUAGE,
      countryToLanguageMap: APP_I18N_COUNTRY_TO_LANGUAGE_MAP,
      commonAssetsPrefix: './assets/i18n/',
      commonNamespace: 'common',
      storageKey: I18N_STORAGE_KEY
    }),
    provideI18nLocaleId(),
    {
      provide: TOAST_CONFIG,
      useValue: {
        maxCount: 5,
        defaultDuration: 3000,
        defaultPosition: 'top-center',
        defaultVariant: 'warning',
        autoClose: true
      } as ToastConfig
    }
  ]
})
export class AppModule {}
